import React, { useMemo, useState } from 'react';
import { Button, Card, Modal } from '../../design-system';
import { useSelected } from '../../providers/selectedContext';
import { useAppContext } from '../../providers/AppContext';
import {
  AnalysisResultsLambda,
  AnalysisResultsLiao,
  LambdaSampleResults,
  LiaoSampleResults,
} from '../../@types/samples/types';
import {
  buildLiaoSampleCurve,
  buildLambdaSampleCurve,
  DEFAULT_SAMPLE_CURVE_POINTS,
} from '../../components/utils/curveGeneration';
import {
  BROOKS_COREY_EXPONENT_LABEL,
  BROOKS_COREY_PORE_SIZE_INDEX_LABEL,
  roundBrooksCoreyDisplayValue,
  roundBrooksCoreyPoreSizeDistributionIndex,
} from '../../utils/brooksCorey';
import styles from './CapillaryPressureSummary.module.css';

type CapillaryPressureSummaryProps = {
  open: boolean;
  onClose: () => void;
};

type VariableRow = {
  label: string;
  value: string;
};

type CurveRow = {
  id: string;
  point: number;
  values: Record<string, string>;
};

type XYPointRow = {
  id: string;
  point: number;
  x: number;
  y: number;
};

type PlottedCurveRow = {
  id: string;
  chart: string;
  pointCount: number;
  values: XYPointRow[];
  note?: string;
};

const ZOOM_LEVELS = [90, 100, 110, 125] as const;
const DEFAULT_ZOOM = 100;
const EMPTY_VALUE = '—';
const DEFAULT_VISIBLE_PLOTTED_ROWS = 6;

const formatLabel = (key: string) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());

const formatValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : EMPTY_VALUE;
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

const formatPointValue = (value: number) => {
  if (!Number.isFinite(value)) return EMPTY_VALUE;
  return Number(value.toFixed(6)).toString();
};

const buildVariableRows = <T extends object>(
  values: T | null | undefined,
  keyOrder?: (keyof T)[],
  labels?: Partial<Record<keyof T, string>>,
): VariableRow[] => {
  if (!values) return [];

  const valueMap = values as Record<string, unknown>;
  const orderedKeys = keyOrder
    ? [...keyOrder, ...Object.keys(valueMap).filter(key => !keyOrder.includes(key as keyof T))]
    : Object.keys(valueMap);

  return orderedKeys.map(key => ({
    label: labels?.[key as keyof T] ?? formatLabel(String(key)),
    value: formatValue(valueMap[String(key)]),
  }));
};

const buildCurveRows = (curves: object): CurveRow[] => {
  const curveMap = curves as Record<string, (number | null)[] | null | undefined>;
  const entries = Object.entries(curveMap).filter(
    ([, values]) => Array.isArray(values) && values.length > 0,
  );

  if (!entries.length) return [];

  const maxLength = Math.max(...entries.map(([, values]) => values?.length ?? 0));

  return Array.from({ length: maxLength }, (_, index) => {
    const values = entries.reduce<Record<string, string>>((accumulator, [key, series]) => {
      accumulator[key] = formatValue(series?.[index]);
      return accumulator;
    }, {});

    return {
      id: `curve-row-${index}`,
      point: index + 1,
      values,
    };
  });
};

const buildXYRows = (
  xValues: (number | null)[] | null | undefined,
  yValues: (number | null)[] | null | undefined,
  filter?: (index: number) => boolean,
): XYPointRow[] => {
  const maxLength = Math.max(xValues?.length ?? 0, yValues?.length ?? 0);
  const rows: XYPointRow[] = [];

  for (let index = 0; index < maxLength; index += 1) {
    if (filter && !filter(index)) continue;

    const x = xValues?.[index];
    const y = yValues?.[index];

    if (typeof x === 'number' && Number.isFinite(x) && typeof y === 'number' && Number.isFinite(y)) {
      rows.push({
        id: `xy-${index}`,
        point: rows.length + 1,
        x,
        y,
      });
    }
  }

  return rows;
};

const buildChartPointRows = (points: { x: number; y: number }[]): XYPointRow[] =>
  points
    .filter(point => Number.isFinite(point.x) && Number.isFinite(point.y))
    .map((point, index) => ({
      id: `point-${index}`,
      point: index + 1,
      x: point.x,
      y: point.y,
    }));

const buildLiaoResampleNote = (sourceCount: number, outputCount: number) =>
  `Starting from ${sourceCount} calculated sample points, it builds a new ${outputCount}-point curve by keeping the anchor point (trueSwi, truePcMax), then creating ${Math.max(outputCount - 1, 0)} evenly spaced Sw values from Swi to 1. For each new Sw, it recomputes PcD with the fitted Liao equation using a, b, and lambda, and converts that PcD back to Pc with pcMax and the minimum PcD-derived pressure.`;

const buildLambdaResampleNote = (sourceCount: number, outputCount: number) =>
  `Starting from ${sourceCount} calculated sample points, it builds a new ${outputCount}-point curve by creating ${outputCount} evenly spaced Sw values from Swi to 1. The first point is fixed at PcMax, and every remaining point is recomputed from the Brooks-Corey equation Pc = Pe × Swd^m on that new saturation grid.`;

const SummaryTable = ({
  columns,
  rows,
  emptyMessage,
  zoom,
  alignRightFrom = 1,
}: {
  columns: string[];
  rows: React.ReactNode[][];
  emptyMessage: string;
  zoom: number;
  alignRightFrom?: number;
}) => (
  <div className={styles.tableWrap}>
    <table
      className={styles.summaryTable}
      style={{ '--summary-zoom': `${zoom / 100}` } as React.CSSProperties}
    >
      <thead>
        <tr>
          {columns.map((column, index) => (
            <th key={column} className={index >= alignRightFrom ? styles.numericCell : undefined}>
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length ? (
          rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className={cellIndex >= alignRightFrom ? styles.numericCell : undefined}>
                  {cell}
                </td>
              ))}
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={columns.length} className={styles.emptyCell}>
              {emptyMessage}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const VariableTableCard = ({
  title,
  subtitle,
  rows,
  zoom,
}: {
  title: string;
  subtitle?: string;
  rows: VariableRow[];
  zoom: number;
}) => (
  <Card variant="outlined" fullWidth>
    <Card.Header title={title} subtitle={subtitle} />
    <Card.Body>
      <SummaryTable
        columns={['Variable', 'Value']}
        rows={rows.map(row => [row.label, row.value])}
        emptyMessage="No values are currently available for this section."
        zoom={zoom}
      />
    </Card.Body>
  </Card>
);

const CurveTableCard = ({
  title,
  subtitle,
  rows,
  zoom,
}: {
  title: string;
  subtitle?: string;
  rows: CurveRow[];
  zoom: number;
}) => {
  const valueKeys = Object.keys(rows[0]?.values ?? {});

  return (
    <Card variant="outlined" fullWidth>
      <Card.Header title={title} subtitle={subtitle} />
      <Card.Body>
        <SummaryTable
          columns={['Point', ...valueKeys.map(formatLabel)]}
          rows={rows.map(row => [row.point, ...valueKeys.map(key => row.values[key])])}
          emptyMessage="No curve values are currently available for this section."
          zoom={zoom}
        />
      </Card.Body>
    </Card>
  );
};

const ValuesTable = ({ rows, zoom }: { rows: XYPointRow[]; zoom: number }) => (
  <div className={styles.valuesTableWrap}>
    <table
      className={styles.valuesTable}
      style={{ '--summary-zoom': `${zoom / 100}` } as React.CSSProperties}
    >
      <thead>
        <tr>
          <th>#</th>
          <th>X</th>
          <th>Y</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr key={row.id}>
            <td>{row.point}</td>
            <td>{formatPointValue(row.x)}</td>
            <td>{formatPointValue(row.y)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PlottedCurvesTable = ({ rows, zoom }: { rows: PlottedCurveRow[]; zoom: number }) => {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  return (
    <div className={styles.tableWrap}>
      <table
        className={`${styles.summaryTable} ${styles.plottedSummaryTable}`}
        style={{ '--summary-zoom': `${zoom / 100}` } as React.CSSProperties}
      >
        <colgroup>
          <col className={styles.plottedChartCol} />
          <col className={styles.plottedCountCol} />
          <col className={styles.plottedValuesCol} />
        </colgroup>
        <thead>
          <tr>
            <th>Chart</th>
            <th className={styles.numericCell}>Point Count</th>
            <th>Values</th>
          </tr>
        </thead>
        <tbody>
          {rows.length ? (
            rows.map(row => {
              const isExpanded = expandedRows[row.id] ?? false;
              const visibleRows = isExpanded ? row.values : row.values.slice(0, DEFAULT_VISIBLE_PLOTTED_ROWS);
              const hasMore = row.values.length > DEFAULT_VISIBLE_PLOTTED_ROWS;

              return (
                <tr key={row.id}>
                  <td>{row.chart}</td>
                  <td className={styles.numericCell}>{row.pointCount}</td>
                  <td>
                    <div className={styles.valuesCell}>
                      {row.note ? <p className={styles.resampleNote}>{row.note}</p> : null}
                      <ValuesTable rows={visibleRows} zoom={zoom} />
                      {hasMore ? (
                        <div className={styles.valuesActions}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setExpandedRows(current => ({
                                ...current,
                                [row.id]: !isExpanded,
                              }))
                            }
                          >
                            {isExpanded
                              ? 'Compress'
                              : `Expand to see more (${row.values.length - DEFAULT_VISIBLE_PLOTTED_ROWS} more)`}
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={3} className={styles.emptyCell}>
                No plotted curves are currently available.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const CapillaryPressureSummary = ({ open, onClose }: CapillaryPressureSummaryProps) => {
  const [zoom, setZoom] = useState<number>(DEFAULT_ZOOM);
  const { selectedInfo } = useSelected();
  const {
    data: { wells },
  } = useAppContext();

  const sample = selectedInfo.sample ?? null;
  const well = wells.find(entry => entry.uid === sample?.well_uid);

  const inputVariableRows = useMemo(() => {
    if (!sample) return [];

    const values: Record<string, unknown> = {
      sample_code: sample.sample_code,
      well: well?.well_name ?? selectedInfo.well?.well_name,
      depth: sample.depth,
      depth_unit: sample.depth_unit,
      sample_length: sample.sample_length,
      permeability: sample.input_sample_values?.permeability,
      porosity: sample.input_sample_values?.porosity,
      fzi: sample.input_sample_values?.fzi,
      swi: sample.input_sample_values?.swi,
      lab_system: sample.lab_system,
      reservoir_system: sample.reservoir_system,
      salinity: sample.input_sample_values?.salinity,
      phi_res_phi_lab_factor: sample.input_sample_values?.phi_res,
      qv: sample.input_sample_values?.qv,
      project: selectedInfo.project?.project_name,
    };

    return buildVariableRows(values);
  }, [sample, selectedInfo.project?.project_name, selectedInfo.well?.well_name, well?.well_name]);

  const inputCurveRows = useMemo(
    () =>
      buildCurveRows({
        sw: sample?.input_sample_curves.pc_model?.sw,
        pc: sample?.input_sample_curves.pc_model?.pc,
      }),
    [sample],
  );

  const liaoValueRows = useMemo(
    () =>
      buildVariableRows<LiaoSampleResults>(sample?.analysis_values.liao, [
        'swir',
        'pc_max',
        'best_a',
        'best_b',
        'lambda_variable',
        'true_swd',
        'true_swir',
        'true_pcd',
        'true_pc_max',
        'fzi',
        'f_value',
        'pce',
        'large_derivative',
      ]),
    [sample],
  );

  const lambdaValueRows = useMemo(() => {
    const values = sample?.analysis_values.lambda_values;
    const rows = buildVariableRows<LambdaSampleResults>(values, [
        'pe',
        'lambda_value',
        'ln_swn_sum',
        'ln_pc_sum',
        'ln_swn_squared_sum',
        'ln_pc_squared_sum',
        'lnswn_x_lnpc_sum',
      ], {
        lambda_value: BROOKS_COREY_EXPONENT_LABEL,
      });

    const poreSizeIndex = roundBrooksCoreyPoreSizeDistributionIndex(
      values?.lambda_value
    );
    const exponentIndex = rows.findIndex(
      row => row.label === BROOKS_COREY_EXPONENT_LABEL
    );
    if (exponentIndex !== -1) {
      rows[exponentIndex].value = formatValue(
        roundBrooksCoreyDisplayValue(values?.lambda_value)
      );
      rows.splice(exponentIndex + 1, 0, {
        label: BROOKS_COREY_PORE_SIZE_INDEX_LABEL,
        value: formatValue(poreSizeIndex),
      });
    }

    return rows;
  }, [sample]);

  const liaoCurveRows = useMemo(
    () => buildCurveRows((sample?.analysis_curves.liao ?? {}) as AnalysisResultsLiao),
    [sample],
  );

  const lambdaCurveRows = useMemo(
    () => buildCurveRows((sample?.analysis_curves.lambda_curves ?? {}) as AnalysisResultsLambda),
    [sample],
  );

  const plottedCurveRows = useMemo<PlottedCurveRow[]>(() => {
    if (!sample) return [];

    const rows: PlottedCurveRow[] = [];
    const liaoCurves = sample.analysis_curves.liao;
    const lambdaCurves = sample.analysis_curves.lambda_curves;
    const lambdaIndexes = lambdaCurves?.indexes ?? null;

    const liaoPressure = liaoCurves?.PceMatch ?? liaoCurves?.Pres;
    const liaoScatter = buildXYRows(liaoCurves?.CorrectedSw, liaoPressure);
    if (liaoScatter.length) {
      rows.push({
        id: 'liao-data-scal',
        chart: 'Liao Chart · Data SCAL',
        pointCount: liaoScatter.length,
        values: liaoScatter,
      });
    }

    const liaoModel = sample.analysis_values.liao
      ? buildChartPointRows(buildLiaoSampleCurve(sample.analysis_values.liao, DEFAULT_SAMPLE_CURVE_POINTS))
      : [];
    if (liaoModel.length) {
      rows.push({
        id: 'liao-pc-liao',
        chart: 'Liao Chart · Pc Liao',
        pointCount: liaoModel.length,
        values: liaoModel,
        note: buildLiaoResampleNote(liaoScatter.length, liaoModel.length),
      });
    }

    const liaoLambdaModel = sample.analysis_values.lambda_values
      ? buildChartPointRows(
          buildLambdaSampleCurve(
            {
              ...sample.analysis_values.lambda_values,
              pc_max: (liaoPressure ?? [])
                .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
                .reduce((max, value) => Math.max(max, value), 0),
            },
            sample.input_sample_values?.swi,
            DEFAULT_SAMPLE_CURVE_POINTS,
          ),
        )
      : [];
    if (liaoLambdaModel.length) {
      rows.push({
        id: 'liao-pc-brooks-corey',
        chart: 'Liao Chart · Pc Brooks-Corey',
        pointCount: liaoLambdaModel.length,
        values: liaoLambdaModel,
        note: buildLambdaResampleNote(liaoScatter.length, liaoLambdaModel.length),
      });
    }

    const lambdaSelectedScatter = buildXYRows(
      lambdaCurves?.CorrectedSw,
      lambdaCurves?.Pres,
      index => (lambdaIndexes ? lambdaIndexes.includes(index) : true),
    );
    if (lambdaSelectedScatter.length) {
      rows.push({
        id: 'lambda-data-scal-selected',
        chart: 'Brooks-Corey Chart · Data SCAL',
        pointCount: lambdaSelectedScatter.length,
        values: lambdaSelectedScatter,
      });
    }

    const lambdaUnselectedScatter = buildXYRows(
      lambdaCurves?.CorrectedSw,
      lambdaCurves?.Pres,
      index => (lambdaIndexes ? !lambdaIndexes.includes(index) : false),
    );
    if (lambdaUnselectedScatter.length) {
      rows.push({
        id: 'lambda-data-scal-unselected',
        chart: 'Brooks-Corey Chart · Data SCAL (not selected)',
        pointCount: lambdaUnselectedScatter.length,
        values: lambdaUnselectedScatter,
      });
    }

    const lambdaModel = sample.analysis_values.lambda_values
      ? buildChartPointRows(
          buildLambdaSampleCurve(
            {
              ...sample.analysis_values.lambda_values,
              pc_max: (lambdaCurves?.Pres ?? [])
                .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
                .reduce((max, value) => Math.max(max, value), 0),
            },
            sample.input_sample_values?.swi,
            DEFAULT_SAMPLE_CURVE_POINTS,
          ),
        )
      : [];
    if (lambdaModel.length) {
      rows.push({
        id: 'lambda-pc-brooks-corey',
        chart: 'Brooks-Corey Chart · Pc model',
        pointCount: lambdaModel.length,
        values: lambdaModel,
        note: buildLambdaResampleNote(lambdaSelectedScatter.length, lambdaModel.length),
      });
    }

    const lambdaLiaoModel = sample.analysis_values.liao
      ? buildChartPointRows(buildLiaoSampleCurve(sample.analysis_values.liao, DEFAULT_SAMPLE_CURVE_POINTS))
      : [];
    if (lambdaLiaoModel.length) {
      rows.push({
        id: 'lambda-pc-liao',
        chart: 'Brooks-Corey Chart · Pc Liao comparison',
        pointCount: lambdaLiaoModel.length,
        values: lambdaLiaoModel,
        note: buildLiaoResampleNote(lambdaSelectedScatter.length, lambdaLiaoModel.length),
      });
    }

    const liaoResultsPrimaryCurve = buildXYRows(liaoCurves?.SwD, liaoCurves?.PcD);
    if (liaoResultsPrimaryCurve.length) {
      rows.push({
        id: 'liao-results-primary-pc',
        chart: 'Liao Results · Primary pc curve',
        pointCount: liaoResultsPrimaryCurve.length,
        values: liaoResultsPrimaryCurve,
      });
    }

    return rows;
  }, [sample]);

  const zoomLabel = `${zoom}%`;

  if (!sample) {
    return null;
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Sample Summary · ${sample.sample_code}`}
      size="lg"
      className={styles.summaryModal}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className={styles.summaryRoot}>
        <div className={styles.summaryTopBar}>
          <p className={styles.summaryIntro}>
            This view consolidates the current sample state used across Capillary Pressure Analysis and shows the exact chart values currently rendered in the Capillary Pressure Chart.
          </p>
          <div className={styles.zoomControls}>
            <span className={styles.zoomLabel}>Zoom {zoomLabel}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setZoom(current => ZOOM_LEVELS[Math.max(0, ZOOM_LEVELS.indexOf(current as 90 | 100 | 110 | 125) - 1)] ?? current)}
              disabled={zoom === ZOOM_LEVELS[0]}
            >
              Zoom Out
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setZoom(DEFAULT_ZOOM)}
              disabled={zoom === DEFAULT_ZOOM}
            >
              Reset
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setZoom(current => ZOOM_LEVELS[Math.min(ZOOM_LEVELS.length - 1, ZOOM_LEVELS.indexOf(current as 90 | 100 | 110 | 125) + 1)] ?? current)}
              disabled={zoom === ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
            >
              Zoom In
            </Button>
          </div>
        </div>

        <div className={styles.sectionStack}>
          <VariableTableCard
            title="Input Variables"
            subtitle="Current values from sample, well, and project context."
            rows={inputVariableRows}
            zoom={zoom}
          />

          <CurveTableCard
            title="Input Curves"
            subtitle="User-provided Sw and Pc values used by the analysis."
            rows={inputCurveRows}
            zoom={zoom}
          />

          <div className={styles.analysisGrid}>
            <VariableTableCard
              title="Calculated Variables · Liao"
              subtitle="Derived variables currently stored on the sample."
              rows={liaoValueRows}
              zoom={zoom}
            />
            <VariableTableCard
              title="Calculated Variables · Brooks-Corey"
              subtitle="Derived variables currently stored on the sample."
              rows={lambdaValueRows}
              zoom={zoom}
            />
          </div>

          <div className={styles.sectionStack}>
            <CurveTableCard
              title="Calculated Curves · Liao"
              subtitle="Persisted analysis curve arrays available on the selected sample."
              rows={liaoCurveRows}
              zoom={zoom}
            />
            <CurveTableCard
              title="Calculated Curves · Brooks-Corey"
              subtitle="Persisted analysis curve arrays available on the selected sample."
              rows={lambdaCurveRows}
              zoom={zoom}
            />
          </div>

          <Card variant="outlined" fullWidth>
            <Card.Header
              title="Plotted Curves"
              subtitle="Exact chart values currently rendered in Capillary Pressure Analysis, including resampled model curves."
            />
            <Card.Body>
              <PlottedCurvesTable rows={plottedCurveRows} zoom={zoom} />
            </Card.Body>
          </Card>
        </div>
      </div>
    </Modal>
  );
};

export default CapillaryPressureSummary;
